/**
 * Utility functions for data loading and processing
 */

import { circleLabPixels } from './colorProcessing'
import { de76 } from './geometry'
import { LabSummary, ProbeResult } from './parameterSelection'

export interface GeoRef {
  zoom: number
  mpp: number // meters per pixel
  x0g: number // global pixel X origin
  y0g: number // global pixel Y origin
  H: number // ROI height in pixels
  W: number // ROI width in pixels
}

/**
 * Converts RGBA ImageData to BGR 3D array format (H x W x 3)
 *
 * @param imageData - ImageData from canvas containing RGBA pixels
 * @returns BGR image array in [B, G, R] format (OpenCV convention)
 */
export function rgbaToBgr(imageData: ImageData): Uint8ClampedArray {
  const { width, height, data } = imageData
  const bgr = new Uint8ClampedArray(width * height * 3)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4
      const bgrIdx = (y * width + x) * 3
      bgr[bgrIdx] = data[idx + 2] // B
      bgr[bgrIdx + 1] = data[idx + 1] // G
      bgr[bgrIdx + 2] = data[idx] // R
    }
  }

  return bgr
}

/**
 * Converts BGR 3D array to RGBA Uint8ClampedArray
 *
 * @param bgr - BGR image array (H x W x 3)
 * @param alpha - Alpha value (default: 255)
 * @returns RGBA flat array suitable for ImageData
 */
export function bgrToRgba(bgr: number[][][], alpha: number = 255): Uint8ClampedArray {
  const height = bgr.length
  const width = bgr[0]?.length || 0
  const rgba = new Uint8ClampedArray(height * width * 4)

  let idx = 0
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const [b, g, r] = bgr[y][x]
      rgba[idx++] = r
      rgba[idx++] = g
      rgba[idx++] = b
      rgba[idx++] = alpha
    }
  }

  return rgba
}

/**
 * Creates a validity mask from ImageData to identify valid pixels
 * Marks pixels as valid (255) or invalid (0) based on their characteristics
 *
 * Invalid pixels typically include:
 * - No-data regions (very light/white pixels common in satellite imagery gaps)
 * - Very dark pixels (possible artifacts or missing data)
 * - Fully transparent pixels
 *
 * @param imageData - ImageData from canvas/tiles
 * @param options - Optional configuration for what constitutes invalid pixels
 * @returns 2D array (H x W) where 255 = valid, 0 = invalid
 */
export function createValidityMask(
  imageData: ImageData,
  options: {
    minBrightness?: number // Min brightness (0-255), below this is invalid
    maxBrightness?: number // Max brightness (0-255), above this is no-data
    minAlpha?: number // Min alpha value (0-255), below this is invalid
    checkNoData?: boolean // Check for no-data pixels (default: true)
  } = {},
): Uint8Array {
  const { width, height, data } = imageData
  const mask: Uint8Array = new Uint8Array(width * height)

  const { minBrightness = 30, maxBrightness = 220, minAlpha = 10, checkNoData = true } = options

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4
      const r = data[idx]
      const g = data[idx + 1]
      const b = data[idx + 2]
      const a = data[idx + 3]

      const brightness = (r + g + b) / 3

      let isValid = true

      if (a < minAlpha) {
        isValid = false
      } else if (checkNoData && brightness > maxBrightness) {
        isValid = false
      } else if (brightness < minBrightness) {
        isValid = false
      }

      mask[y * width + x] = isValid ? 255 : 0
    }
  }

  return mask
}

/**
 * Checks if two lengths are within a percentage of each other
 * Used in inward candidate detection
 *
 * @param a - First length
 * @param b - Second length
 * @param pct - Percentage threshold (0-1)
 * @returns True if within percentage
 */
export function withinPct(a: number, b: number, pct: number): boolean {
  if (a <= 1e-6 || b <= 1e-6) {
    return false
  }
  return Math.abs(a - b) / Math.max(a, b) <= pct
}

/**
 * Computes median length of non-runner rays
 *
 * @param edgePoints - Map of direction to endpoint
 * @param results - Map of direction to ray walk result
 * @param centerXy - Center point [x, y]
 * @param ranToEndCheck - Function to check if ray ran to end
 * @param rayLenPx - Function to compute ray length
 * @returns Median length or null if no valid rays
 */
export function medianLenNonRunners(
  edgePoints: Record<string, [number, number]>,
  results: Record<string, unknown>,
  centerXy: [number, number],
  ranToEndCheck: (res: unknown) => boolean,
  rayLenPx: (edgePoints: Record<string, [number, number]>, center: [number, number], name: string) => number | null,
): number | null {
  const lens: number[] = []

  for (let i = 1; i <= 16; i++) {
    const k = String(i)
    if (ranToEndCheck(results[k])) {
      continue
    }
    const lm = rayLenPx(edgePoints, centerXy, k)
    if (lm !== null) {
      lens.push(lm)
    }
  }

  if (lens.length === 0) {
    return null
  }

  lens.sort((a, b) => a - b)
  const mid = Math.floor(lens.length / 2)

  return lens.length % 2 === 1 ? lens[mid] : (lens[mid - 1] + lens[mid]) / 2
}

/**
 * Checks if a ray is abnormally short compared to global median
 *
 * @param edgePoints - Map of direction to endpoint
 * @param results - Map of direction to ray walk result
 * @param centerXy - Center point [x, y]
 * @param name - Direction name
 * @param frac - Fraction threshold (default 0.50)
 * @param medianLen - Precomputed median length (optional)
 * @param ranToEndCheck - Function to check if ray ran to end
 * @param rayLenPx - Function to compute ray length
 * @returns True if abnormally short
 */
export function isAbnormallyShortGlobal(
  edgePoints: Record<string, [number, number]>,
  results: Record<string, unknown>,
  centerXy: [number, number],
  name: string,
  frac: number = 0.5,
  medianLen: number | null,
  ranToEndCheck: (res: unknown) => boolean,
  rayLenPx: (edgePoints: Record<string, [number, number]>, center: [number, number], name: string) => number | null,
  medianLenCompute: (
    edgePoints: Record<string, [number, number]>,
    results: Record<string, unknown>,
    centerXy: [number, number],
    ranToEndCheck: (res: unknown) => boolean,
    rayLenPx: (edgePoints: Record<string, [number, number]>, center: [number, number], name: string) => number | null,
  ) => number | null,
): boolean {
  const lm = rayLenPx(edgePoints, centerXy, name)
  if (lm === null) {
    return false
  }

  if (medianLen === null) {
    medianLen = medianLenCompute(edgePoints, results, centerXy, ranToEndCheck, rayLenPx)
  }

  if (medianLen === null || medianLen <= 1e-6) {
    return false
  }

  return lm < frac * medianLen
}

/** Compute percentile along axis 0 for Nx3 LAB array (stored as flat Float32Array) */
export function percentileAxis0(lab: Float32Array, n: number, pct: number): Float32Array {
  const result = new Float32Array(3)
  for (let ch = 0; ch < 3; ch++) {
    const vals: number[] = []
    for (let i = 0; i < n; i++) {
      vals.push(lab[i * 3 + ch])
    }
    vals.sort((a, b) => a - b)
    const idx = (pct / 100) * (vals.length - 1)
    const lo = Math.floor(idx)
    const hi = Math.ceil(idx)
    const frac = idx - lo
    result[ch] = vals[lo] * (1 - frac) + vals[hi] * frac
  }
  return result
}

export function robustLabSummary(labPixels: Float32Array): LabSummary {
  const n = labPixels.length / 3
  const med = percentileAxis0(labPixels, n, 50)
  const p16 = percentileAxis0(labPixels, n, 16)
  const p84 = percentileAxis0(labPixels, n, 84)
  const spread = new Float32Array([p84[0] - p16[0], p84[1] - p16[1], p84[2] - p16[2]])
  return { med, p16, p84, spread, n }
}

export function probeGreenTouchingCircles(
  roiRgba: ImageData,
  W: number,
  H: number,
  validMask: Uint8Array | null,
  centerPx: [number, number],
  georef: GeoRef,
  probeRadiusM: number,
  minPix: number,
): ProbeResult | null {
  const mpp = georef.mpp
  const rPx = probeRadiusM / mpp
  const offPx = (2.0 * probeRadiusM) / mpp
  const [cx, cy] = centerPx

  const probes: Record<string, [number, number]> = {
    C: [cx, cy],
    N: [cx, cy - offPx],
    E: [cx + offPx, cy],
    S: [cx, cy + offPx],
    W: [cx - offPx, cy],
  }

  const summaries: Record<string, LabSummary | null> = {}
  const droppedMinpix: Record<string, number> = {}

  for (const [k, [px, py]] of Object.entries(probes)) {
    const lab = circleLabPixels(roiRgba, W, H, validMask, px, py, rPx)
    const nPix = lab ? lab.length / 3 : 0

    if (!lab || nPix < minPix) {
      summaries[k] = null
      droppedMinpix[k] = nPix
      continue
    }
    summaries[k] = robustLabSummary(lab)
  }

  if (!summaries.C) {
    return null
  }
  const centerMed = summaries.C!.med

  // ΔE cutoff filtering for outer circles
  const OUTER_DE_CUTOFF = 16.0
  const outerKeys = ['N', 'E', 'S', 'W'].filter((k) => summaries[k] !== null)
  const outerScores: Record<string, number> = {}
  for (const k of outerKeys) {
    outerScores[k] = de76(summaries[k]!.med, centerMed)
  }

  const keptOuter: string[] = []
  const droppedByDe: Record<string, [number, number]> = {}
  for (const k of outerKeys) {
    if (outerScores[k] <= OUTER_DE_CUTOFF) {
      keptOuter.push(k)
    } else {
      droppedByDe[k] = [outerScores[k], outerScores[k] - OUTER_DE_CUTOFF]
    }
  }

  const keptKeys = ['C', ...keptOuter.filter((k) => summaries[k] !== null)]

  // Compute selection stats
  const selMeds = keptKeys.map((k) => summaries[k]!.med)

  // Baseline = median of kept medians
  const baseline = new Float32Array(3)
  for (let ch = 0; ch < 3; ch++) {
    const vals = selMeds.map((m) => m[ch]).sort((a, b) => a - b)
    const mid = Math.floor(vals.length / 2)
    baseline[ch] = vals.length % 2 === 1 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2
  }

  // Std of diffs
  const diffs = selMeds.map((m) => [m[0] - baseline[0], m[1] - baseline[1], m[2] - baseline[2]])
  const stdMeds = new Float32Array(3)
  for (let ch = 0; ch < 3; ch++) {
    const vals = diffs.map((d) => d[ch])
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length
    const variance = vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length
    stdMeds[ch] = Math.sqrt(variance)
  }

  let maxAbsAb = 0
  for (const d of diffs) {
    maxAbsAb = Math.max(maxAbsAb, Math.abs(d[1]), Math.abs(d[2]))
  }

  return {
    probeRadiusM,
    offsetM: 2.0 * probeRadiusM,
    summaries,
    centerMed,
    outerScoresToCenter: outerScores,
    outerDeCutoff: OUTER_DE_CUTOFF,
    keptKeys,
    droppedMinpix,
    droppedByDe,
    baselineLab: baseline,
    stdMedsKept: stdMeds,
    maxAbsAbKept: maxAbsAb,
    mpp,
  }
}
